import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Plus,
  Package,
  Edit2,
  Trash2,
  RefreshCw,
  Calendar,
  Globe,
  Search,
} from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";

const API_BASE = "https://api.sobevenda.com.br/api-proxy/products";

function getToken() {
  return localStorage.getItem("sobevenda_token") || "";
}

function fmtDate(dt) {
  if (!dt) return "-";
  try {
    return new Date(dt).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(dt);
  }
}

function fmtCurrency(price, currency) {
  const symbols = { USD: "$", EUR: "€", GBP: "£", CAD: "C$", AUD: "A$", BRL: "R$" };
  return `${symbols[currency] || "$"}${parseFloat(price).toFixed(2)}`;
}

export default function SobeProducts() {
  const navigate = useNavigate();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [filterNiche, setFilterNiche] = useState("");
  const [filterCurrency, setFilterCurrency] = useState("");

  // Confirmação de exclusão
  const [confirmId, setConfirmId] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/list`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar produtos");
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : (data.products || []));
    } catch (err) {
      setError(err.message || "Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();

    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(""), 5000);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      const res = await fetch(`${API_BASE}/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Erro ao deletar produto");
      setProducts((prev) => prev.filter((p) => p.id !== id));
      setSuccessMessage("Produto removido com sucesso.");
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      setError(err.message || "Erro ao deletar produto");
    }
  };

  const uniqueNiches = [...new Set(products.map((p) => p.niche).filter(Boolean))];
  const uniqueCurrencies = [...new Set(products.map((p) => p.currency).filter(Boolean))];

  const filteredProducts = products.filter((p) => {
    const matchSearch =
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.niche?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchNiche = !filterNiche || p.niche === filterNiche;
    const matchCurrency = !filterCurrency || p.currency === filterCurrency;
    return matchSearch && matchNiche && matchCurrency;
  });

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Package className="w-6 h-6" />
              Produtos
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Gerencie os produtos vinculados às suas campanhas.
            </p>
          </div>

          <button
            onClick={fetchProducts}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 shrink-0 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>

        {/* MENSAGEM DE SUCESSO */}
        {successMessage ? (
          <div className="p-3 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900">
            {successMessage}
          </div>
        ) : null}

        {/* MENSAGEM DE ERRO */}
        {error ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm dark:bg-red-900/20 dark:text-red-400 dark:border-red-900">
            {error}
          </div>
        ) : null}

        {/* AÇÃO PRIMÁRIA + CONTADOR */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {loading
              ? "Carregando produtos..."
              : `${filteredProducts.length} ${filteredProducts.length === 1 ? "produto" : "produtos"}${
                  filteredProducts.length !== products.length
                    ? ` de ${products.length}`
                    : ""
                }`}
          </p>

          <button
            onClick={() => navigate("/produtos/novo")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <Plus className="w-4 h-4" />
            Adicionar produto
          </button>
        </div>

        {/* FILTROS */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            {/* Busca */}
            <div className="md:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou nicho..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
              />
            </div>

            {/* Nicho */}
            <div className="md:col-span-4">
              <select
                value={filterNiche}
                onChange={(e) => setFilterNiche(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100"
              >
                <option value="">Todos os nichos</option>
                {uniqueNiches.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            {/* Moeda */}
            <div className="md:col-span-3">
              <select
                value={filterCurrency}
                onChange={(e) => setFilterCurrency(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100"
              >
                <option value="">Todas as moedas</option>
                {uniqueCurrencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* LISTAGEM — CARDS NO MOBILE / TABELA NO DESKTOP */}

        {/* Mobile/Tablet: Cards (visível < lg) */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500 text-sm">
              Carregando produtos...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {searchTerm || filterNiche || filterCurrency
                  ? "Nenhum produto encontrado com esses filtros."
                  : "Nenhum produto cadastrado ainda."}
              </p>
              {!searchTerm && !filterNiche && !filterCurrency && (
                <button
                  onClick={() => navigate("/produtos/novo")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar produto
                </button>
              )}
            </div>
          ) : (
            filteredProducts.map((product) => (
              <div
                key={product.id}
                className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
              >
                {/* Nome + preço */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Produto</div>
                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100 break-words">
                      {product.name}
                    </div>
                  </div>
                  <span className="shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    {fmtCurrency(product.price, product.currency)}
                  </span>
                </div>

                {/* Detalhes */}
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Nicho</div>
                    <div className="text-slate-700 dark:text-slate-300 break-words">{product.niche || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Moeda</div>
                    <div className="text-slate-700 dark:text-slate-300">{product.currency || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Criado em</div>
                    <div className="text-slate-600 dark:text-slate-300">{fmtDate(product.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Frete grátis</div>
                    <div className="text-slate-700 dark:text-slate-300">{product.free_shipping ? "Sim" : "Não"}</div>
                  </div>
                </div>

                {/* Mini stats */}
                <div className="grid grid-cols-4 gap-2 mb-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40">
                  {[
                    { label: "Headlines", value: product.headlines?.length || 0 },
                    { label: "Descriptions", value: product.descriptions?.length || 0 },
                    { label: "Keywords", value: product.keywords?.length || 0 },
                    { label: "Extensions", value: (product.sitelinks?.length || 0) + (product.callouts?.length || 0) + (product.price_extensions?.length || 0) },
                  ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                      <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{value}</div>
                      <div className="text-[10px] text-slate-500">{label}</div>
                    </div>
                  ))}
                </div>

                {/* Ações */}
                <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => navigate(`/produtos/editar/${product.id}`)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    <Edit2 className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => setConfirmId(product.id)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remover
                  </button>
                </div>

                <div className="text-[11px] text-slate-400 text-right mt-2">ID: {product.id}</div>
              </div>
            ))
          )}
        </div>

        {/* Desktop: Tabela (visível >= lg) */}
        <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40">
                <tr className="text-left">
                  <th className="px-4 py-3 w-[20%] font-semibold text-slate-700 dark:text-slate-200">Nome</th>
                  <th className="px-4 py-3 w-[10%] font-semibold text-slate-700 dark:text-slate-200">Preço</th>
                  <th className="px-4 py-3 w-[10%] font-semibold text-slate-700 dark:text-slate-200">Nicho</th>
                  <th className="px-4 py-3 w-[7%] font-semibold text-slate-700 dark:text-slate-200">Moeda</th>
                  <th className="px-4 py-3 w-[25%] font-semibold text-slate-700 dark:text-slate-200">Conteúdo</th>
                  <th className="px-4 py-3 w-[13%] font-semibold text-slate-700 dark:text-slate-200">Criado</th>
                  <th className="px-4 py-3 w-[15%] text-right font-semibold text-slate-700 dark:text-slate-200">Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-4 text-slate-500 text-sm" colSpan={7}>
                      Carregando produtos...
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-sm" colSpan={7}>
                      <div className="text-center">
                        <p className="text-slate-500 mb-4">
                          {searchTerm || filterNiche || filterCurrency
                            ? "Nenhum produto encontrado com esses filtros."
                            : "Nenhum produto cadastrado ainda."}
                        </p>
                        {!searchTerm && !filterNiche && !filterCurrency && (
                          <button
                            onClick={() => navigate("/produtos/novo")}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                          >
                            <Plus className="w-4 h-4" />
                            Adicionar produto
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-t border-slate-100 dark:border-slate-800 align-top"
                    >
                      {/* Nome */}
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 dark:text-slate-100 break-words">
                          {product.name}
                        </div>
                        {product.producer_url && (
                          <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                            <Globe className="w-3 h-3" />
                            <span className="truncate max-w-[160px]">{product.producer_url}</span>
                          </div>
                        )}
                      </td>

                      {/* Preço */}
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        {fmtCurrency(product.price, product.currency)}
                      </td>

                      {/* Nicho */}
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300 break-words">
                        {product.niche || "-"}
                      </td>

                      {/* Moeda */}
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                        {product.currency || "-"}
                      </td>

                      {/* Conteúdo (mini stats) */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
                          <span><b className="text-slate-700 dark:text-slate-200">{product.headlines?.length || 0}</b> headlines</span>
                          <span><b className="text-slate-700 dark:text-slate-200">{product.descriptions?.length || 0}</b> descriptions</span>
                          <span><b className="text-slate-700 dark:text-slate-200">{product.keywords?.length || 0}</b> keywords</span>
                          <span>
                            <b className="text-slate-700 dark:text-slate-200">
                              {(product.sitelinks?.length || 0) + (product.callouts?.length || 0) + (product.price_extensions?.length || 0)}
                            </b> extensions
                          </span>
                        </div>
                      </td>

                      {/* Data */}
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {fmtDate(product.created_at)}
                        </div>
                      </td>

                      {/* Ações */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/produtos/editar/${product.id}`)}
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                            title="Editar produto"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setConfirmId(product.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remover
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* MODAL DE CONFIRMAÇÃO */}
      <ConfirmDialog
        open={confirmId !== null}
        title="Remover produto"
        message="Tem certeza que deseja remover este produto? Esta ação não pode ser desfeita."
        confirmLabel="Remover"
        onConfirm={handleDelete}
        onCancel={() => setConfirmId(null)}
      />
    </div>
  );
}